import CriteriaEditor from '../components/ai-review/CriteriaEditor';
import {AcceptanceCriteria,acceptanceCriteria} from '../components/ai-review/assessment';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { Plus, Save, X, RefreshCw, Search } from 'lucide-react';
import { PageContainer, PageHeader } from '../components/layout/Page';

interface DocumentDefinition {
  id: string;
  name: string;
  description: string | null;
  acceptance_criteria?: unknown;
  category_id: string | null;
  organization_id: string | null;
  renewal_type: 'none' | 'monthly' | 'yearly' | 'fixed_date' | 'custom_interval';
  renewal_interval: unknown | null;
  fixed_expiry_date: string | null;
  is_active: boolean;
  is_moral: boolean | null;
  is_required?: boolean;
  created_at: string;
  updated_at: string;
  category?: {
    id: string;
    name: string;
  } | null;
  organization?: {
    id: string;
    name: string;
  } | null;
}

interface Category {
  id: string;
  name: string;
}

interface Organization {
  id: string;
  name: string;
}

export default function DocumentDefinitions() {
  const { user } = useAuth();
  const [definitions, setDefinitions] = useState<DocumentDefinition[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  
  // Form states
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingDefinition, setEditingDefinition] = useState<DocumentDefinition | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category_id: '',
    organization_id: '',
    renewal_type: 'none' as 'none' | 'monthly' | 'yearly' | 'fixed_date' | 'custom_interval',
    renewal_interval_months: '',
    fixed_expiry_date: '',
    is_active: true,
    is_moral: null as boolean | null,
    is_required: false
  });
  const [criteria,setCriteria]=useState<AcceptanceCriteria|null>(null);
  const [criteriaDirty,setCriteriaDirty]=useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (user) {
      fetchDefinitions();
      fetchCategories();
      fetchOrganizations();
    }
  }, [user]);

  const fetchDefinitions = async () => {
    try {
      setLoading(true);
      setLoadError(null);

      let query = supabase
        .from('document_definitions')
        .select(`
          *,
          category:categories(id, name),
          organization:organizations(id, name)
        `)
        .order('name', { ascending: true });

      const { data, error } = await query;

      if (error) throw error;
      setDefinitions(data || []);
    } catch (error) {
      console.error('Error fetching definitions:', error);
      setLoadError('No se pudo cargar el catálogo. Intenta actualizar de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('categories')
        .select('id, name')
        .order('name', { ascending: true });

      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const { data, error } = await supabase
        .from('organizations')
        .select('id, name')
        .order('name', { ascending: true });

      if (error) throw error;
      setOrganizations(data || []);
    } catch (error) {
      console.error('Error fetching organizations:', error);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchDefinitions();
    setRefreshing(false);
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      errors.name = 'El nombre es requerido';
    }

    if (!formData.category_id) {
      errors.category_id = 'La categoría es requerida';
    }

    if (formData.renewal_type === 'custom_interval' && !formData.renewal_interval_months) {
      errors.renewal_interval_months = 'El intervalo de meses es requerido';
    }

    if (formData.renewal_type === 'fixed_date' && !formData.fixed_expiry_date) {
      errors.fixed_expiry_date = 'La fecha de vencimiento es requerida';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      const definitionData: any = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        category_id: formData.category_id || null,
        organization_id: formData.organization_id || null,
        renewal_type: formData.renewal_type,
        renewal_interval: formData.renewal_type === 'custom_interval' && formData.renewal_interval_months
          ? `${formData.renewal_interval_months} mons`
          : null,
        fixed_expiry_date: formData.fixed_expiry_date || null,
        is_active: formData.is_active,
        is_moral: formData.is_moral,
        is_required: formData.is_required,
        created_by_user_id: user!.id
      };

      if(criteriaDirty) {
        if(criteria&&(!criteria.criteria.length||criteria.criteria.some(c=>!c.text.trim())))throw new Error('Añade al menos un criterio y completa su descripción.');
        definitionData.acceptance_criteria=criteria?{...criteria,status:'custom',version:(acceptanceCriteria(editingDefinition?.acceptance_criteria)?.version || 0)+1}:null;
      }
      if (editingDefinition) {
        // Update existing definition
        const { error } = await supabase
          .from('document_definitions')
          .update(definitionData)
          .eq('id', editingDefinition.id);

        if (error) throw error;
        alert('Definición actualizada exitosamente');
      } else {
        // Create new definition
        const { error } = await supabase
          .from('document_definitions')
          .insert([definitionData]);

        if (error) throw error;
        alert('Definición creada exitosamente');
      }

      // Reset form and refresh
      setShowAddForm(false);
      setEditingDefinition(null);
      resetForm();
      await fetchDefinitions();
    } catch (error: any) {
      console.error('Error saving definition:', error);
      alert(error.message || 'Error al guardar la definición');
    }
  };

  const handleEdit = (definition: DocumentDefinition) => {
    setEditingDefinition(definition);
    setCriteria(acceptanceCriteria(definition.acceptance_criteria));setCriteriaDirty(false);
    // Extract the number of months from the interval string (e.g., '2 mons' -> '2')
    let intervalMonths = '';
    if (definition.renewal_interval) {
      if (typeof definition.renewal_interval === 'string') {
        const match = definition.renewal_interval.match(/^(\d+)\s+(?:mons?|months?)\b/);
        intervalMonths = match ? match[1] : '';
      } else if (typeof definition.renewal_interval === 'object') {
        // Handle legacy format if it exists
        const interval = definition.renewal_interval as { months?: number; days?: number };
        intervalMonths = interval.months?.toString() || (interval.days ? Math.round(interval.days / 30).toString() : '');
      }
    }
    
    setFormData({
      name: definition.name,
      description: definition.description || '',
      category_id: definition.category_id || '',
      organization_id: definition.organization_id || '',
      renewal_type: definition.renewal_type,
      renewal_interval_months: intervalMonths,
      fixed_expiry_date: definition.fixed_expiry_date || '',
      is_active: definition.is_active,
      is_moral: definition.is_moral,
      is_required: definition.is_required ?? false
    });
    setShowAddForm(true);
    
    // Scroll to top to show the form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (definitionId: string) => {
    if (!window.confirm('¿Está seguro de eliminar esta definición? Esto podría afectar los documentos existentes.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('document_definitions')
        .delete()
        .eq('id', definitionId);

      if (error) throw error;

      alert('Definición eliminada exitosamente');
      await fetchDefinitions();
    } catch (error: any) {
      console.error('Error deleting definition:', error);
      alert(error.message || 'Error al eliminar la definición');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category_id: '',
      organization_id: '',
      renewal_type: 'none',
      renewal_interval_months: '',
      fixed_expiry_date: '',
      is_active: true,
      is_moral: null,
      is_required: false
    });
    setFormErrors({});setCriteria(null);setCriteriaDirty(false);
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setEditingDefinition(null);
    resetForm();
  };

  const getRenewalTypeText = (type: string) => {
    switch (type) {
      case 'none':
        return 'Sin renovación';
      case 'monthly':
        return 'Mensual';
      case 'yearly':
        return 'Anual';
      case 'fixed_date':
        return 'Fecha fija';
      case 'custom_interval':
        return 'Intervalo personalizado';
      default:
        return type;
    }
  };

  const filteredDefinitions = definitions.filter(def => {
    const matchesSearch = searchTerm === '' || 
      def.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (def.description && def.description.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesSearch && (categoryFilter === 'all' || def.category_id === categoryFilter);
  });

  return (
    <PageContainer>
        <PageHeader
          title="Definiciones"
          description="Configura los documentos, su vigencia y a quiénes aplican."
          actions={<>
            <button
              onClick={handleRefresh}
              disabled={loading || refreshing}
              className="flex items-center gap-2 px-3 py-2 border border-slate-300 bg-white text-sm rounded-lg hover:bg-slate-100 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Actualizar
            </button>
            <button
              onClick={() => {
                handleCancel();
                setShowAddForm(true);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-700 text-white text-sm font-medium rounded-lg hover:bg-blue-800"
            >
              <Plus className="w-4 h-4" />
              Nueva Definición
            </button>
          </>}
        />

        {loadError && <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">{loadError}</p>}
        {/* Filters */}
        <div className="bg-white p-4 rounded-xl border border-slate-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="search"
                aria-label="Buscar en el catálogo"
                placeholder="Buscar definiciones..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <select
                aria-label="Filtrar por categoría"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">Todas las categorías</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>

            <div className="text-right text-sm text-gray-600 flex items-center justify-end">
              {filteredDefinitions.length} definición{filteredDefinitions.length !== 1 ? 'es' : ''}
            </div>
          </div>
        </div>

        {/* Add/Edit Form */}
        {showAddForm && (
          <div className="bg-white p-6 rounded-xl border border-slate-200">
            <h2 className="text-xl font-semibold mb-4">
              {editingDefinition ? 'Editar Definición' : 'Nueva Definición'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <CriteriaEditor value={criteria} onChange={value=>{setCriteria(value);setCriteriaDirty(true);}}/>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre *
                  </label>
                  <input
                    type="text"
                    aria-label="Nombre" value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      formErrors.name ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {formErrors.name && (
                    <p className="text-red-500 text-xs mt-1">{formErrors.name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Categoría *
                  </label>
                  <select
                    aria-label="Categoría" value={formData.category_id}
                    onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      formErrors.category_id ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Seleccionar categoría</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                  {formErrors.category_id && (
                    <p className="text-red-500 text-xs mt-1">{formErrors.category_id}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Organización
                  </label>
                  <select
                    aria-label="Organización" value={formData.organization_id}
                    onChange={(e) => setFormData({ ...formData, organization_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Sin organización específica</option>
                    {organizations.map(org => (
                      <option key={org.id} value={org.id}>{org.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Aplica para tipo de persona
                  </label>
                  <select
                    value={formData.is_moral === null ? 'both' : formData.is_moral ? 'moral' : 'fisico'}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData({
                        ...formData,
                        is_moral: value === 'both' ? null : value === 'moral'
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="both">Ambos (Física y Moral)</option>
                    <option value="fisico">Solo Persona Física</option>
                    <option value="moral">Solo Persona Moral</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Aceptar sin documento
                  </label>
                  <select
                    value={formData.is_required ? 'false' : 'true'}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData({
                        ...formData,
                        is_required: value === 'false'
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="true">Permitir</option>
                    <option value="false">No Permitir</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo de Renovación
                  </label>
                  <select
                    aria-label="Renovación" value={formData.renewal_type}
                    onChange={(e) => setFormData({ ...formData, renewal_type: e.target.value as any })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="none">Sin renovación</option>
                    <option value="monthly">Mensual</option>
                    <option value="yearly">Anual</option>
                    <option value="fixed_date">Fecha fija</option>
                    <option value="custom_interval">Intervalo personalizado</option>
                  </select>
                </div>

                {formData.renewal_type === 'custom_interval' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Intervalo de meses *
                    </label>
                    <input
                      type="number"
                      aria-label="Intervalo en meses" value={formData.renewal_interval_months}
                      onChange={(e) => setFormData({ ...formData, renewal_interval_months: e.target.value })}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        formErrors.renewal_interval_months ? 'border-red-500' : 'border-gray-300'
                      }`}
                      min="1"
                    />
                    {formErrors.renewal_interval_months && (
                      <p className="text-red-500 text-xs mt-1">{formErrors.renewal_interval_months}</p>
                    )}
                  </div>
                )}

                {formData.renewal_type === 'fixed_date' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Fecha de vencimiento fija *
                    </label>
                    <input
                      type="date"
                      aria-label="Fecha de vencimiento" value={formData.fixed_expiry_date}
                      onChange={(e) => setFormData({ ...formData, fixed_expiry_date: e.target.value })}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        formErrors.fixed_expiry_date ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {formErrors.fixed_expiry_date && (
                      <p className="text-red-500 text-xs mt-1">{formErrors.fixed_expiry_date}</p>
                    )}
                  </div>
                )}


                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descripción
                  </label>
                  <textarea
                    aria-label="Descripción" value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                  />
                </div>

                <div className="col-span-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Definición activa
                    </span>
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-6">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  <X className="w-4 h-4" />
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex items-center gap-2 px-4 py-2 bg-blue-700 text-white text-sm font-medium rounded-lg hover:bg-blue-800"
                >
                  <Save className="w-4 h-4" />
                  {editingDefinition ? 'Actualizar' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-left text-sm" aria-label="Definiciones de documentos">
              <thead className="border-b border-slate-200 bg-slate-50 text-xs font-medium text-slate-500"><tr>
                <th scope="col" className="px-5 py-3">Documento</th><th scope="col" className="px-5 py-3">Categoría</th><th scope="col" className="px-5 py-3">Renovación</th><th scope="col" className="px-5 py-3">Aplica a</th><th scope="col" className="px-5 py-3">Estado</th><th scope="col" className="px-5 py-3 text-right">Acciones</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? <tr><td colSpan={6} className="p-8 text-center text-slate-500" role="status">Cargando definiciones…</td></tr> : filteredDefinitions.map(def => <tr key={def.id} className="hover:bg-slate-50/70">
                  <td className="max-w-sm px-5 py-4"><div className="font-medium text-slate-900">{def.name}</div><p className="mt-1 text-xs text-slate-500">{def.is_required ? 'Obligatorio' : 'Opcional'} · {def.organization?.name || 'Sin organización'}</p>{def.description && <details className="mt-1 text-xs text-slate-500"><summary className="cursor-pointer">Descripción</summary><p className="mt-2 whitespace-pre-wrap">{def.description}</p></details>}</td>
                  <td className="px-5 py-4 text-slate-600">{def.category?.name || 'Sin categoría'}</td>
                  <td className="px-5 py-4 text-slate-600">{def.renewal_type === 'custom_interval' && typeof def.renewal_interval === 'string' ? `Cada ${def.renewal_interval.replace(/mons?\b|months?\b/g, 'meses')}` : getRenewalTypeText(def.renewal_type)}{def.renewal_type === 'fixed_date' && def.fixed_expiry_date && <span className="mt-1 block text-xs">{format(new Date(def.fixed_expiry_date), 'dd/MM/yyyy', {locale: es})}</span>}</td>
                  <td className="px-5 py-4 text-slate-600">{def.is_moral === null ? 'Todas las personas' : def.is_moral ? 'Persona moral' : 'Persona física'}</td>
                  <td className="px-5 py-4"><span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${def.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>{def.is_active ? 'Activo' : 'Inactivo'}</span></td>
                  <td className="px-5 py-4 text-right"><div className="flex items-center justify-end gap-3"><button onClick={() => handleEdit(def)} className="rounded-lg border border-slate-300 px-3 py-2 hover:bg-slate-100" aria-label={`Editar ${def.name}`}>Editar</button><details className="relative"><summary aria-label={`Más opciones para ${def.name}`} className="cursor-pointer rounded px-2 py-2 text-slate-500">Más</summary><button onClick={() => handleDelete(def.id)} className="mt-2 block rounded-lg border border-red-200 bg-white px-4 py-2 text-red-700">Eliminar</button></details></div></td>
                </tr>)}
                {!loading && !loadError && !filteredDefinitions.length && <tr><td colSpan={6} className="p-10 text-center text-slate-500">No hay definiciones que coincidan con los filtros.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
    </PageContainer>
  );
}
